import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createArena, getArenaBranches } from '../../api/gestor';
import { Modal, useToast } from './ui';

export function NewArenaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [court, setCourt] = useState('');
  const [branch, setBranch] = useState('');
  const [baseBranch, setBaseBranch] = useState('prime');
  const [state, setState] = useState('mg');
  const [cityBucket, setCityBucket] = useState('belohorizonte');
  const [cityDisplay, setCityDisplay] = useState('Belo Horizonte');
  const [numQuadras, setNumQuadras] = useState(2);
  const [names, setNames] = useState<string[]>(['Quadra1', 'Quadra2']);
  const [valor, setValor] = useState('');
  const [diaCobranca, setDiaCobranca] = useState('5');
  const [contato, setContato] = useState('');
  const [status, setStatus] = useState<'rodando' | 'a_implantar'>('rodando');
  const [branchMode, setBranchMode] = useState<'create' | 'existing'>('create');

  const { data: branches } = useQuery({
    queryKey: ['arena-branches'],
    queryFn: getArenaBranches,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const [result, setResult] = useState<{ steps: string[]; next: string } | null>(null);

  const setNum = (n: number) => {
    const clamped = Math.max(1, Math.min(6, n || 1));
    setNumQuadras(clamped);
    setNames((prev) => Array.from({ length: clamped }, (_, i) => prev[i] || `Quadra${i + 1}`));
  };

  const create = useMutation({
    mutationFn: createArena,
    onSuccess: (r) => {
      setResult(r);
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast(`Arena criada: branch ${r.branch}`);
    },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });

  const submit = () => {
    if (branchMode === 'existing') {
      if (!branch.trim()) {
        toast('Selecione a branch existente', 'warn');
        return;
      }
      create.mutate({
        status,
        branchMode: 'existing',
        branch: branch.trim(),
        cityDisplay: cityDisplay.trim() || undefined,
        court: court.trim() || branch.trim(),
        cityBucket: cityBucket.trim() || branch.trim(),
        subcourts: [],
        billing: contato.trim() && valor
          ? { nome: court.trim() || undefined, contato: contato.replace(/\D/g, ''), valor, diaCobranca: Number(diaCobranca) || 5 }
          : undefined,
      });
      return;
    }
    const faltando = [
      !court.trim() && 'nome da arena',
      !branch.trim() && 'branch nova',
      !cityBucket.trim() && 'cidade (bucket)',
    ].filter(Boolean);
    if (faltando.length) {
      toast(`Falta preencher: ${faltando.join(', ')}`, 'warn');
      return;
    }
    create.mutate({
      status,
      branchMode: 'create',
      branch: branch.trim().toLowerCase(),
      baseBranch: baseBranch.trim() || 'prime',
      state: state.trim().toLowerCase(),
      cityBucket: cityBucket.trim().toLowerCase().replace(/\s/g, ''),
      cityDisplay: cityDisplay.trim() || undefined,
      court: court.trim(),
      subcourts: names.map((n) => n.trim()).filter(Boolean),
      billing: contato.trim() && valor
        ? { nome: court.trim(), contato: contato.replace(/\D/g, ''), valor, diaCobranca: Number(diaCobranca) || 5 }
        : undefined,
    });
  };

  if (result) {
    return (
      <Modal title="Arena criada!" open={open}
        onClose={() => { setResult(null); onClose(); }}
        onConfirm={() => { setResult(null); onClose(); }} confirmText="Fechar">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>
          {result.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
        <p style={{ marginTop: 14, fontSize: 13, color: 'var(--ft-green)' }}>{result.next}</p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Criar nova arena"
      desc="Cria a branch a partir de uma estável, ajusta os scripts das câmeras, cria a branch no ApertaiSponsorship, o cliente no Firestore e a linha na planilha de cobrança."
      open={open} onClose={onClose} onConfirm={submit}
      confirmText="Criar arena" busy={create.isPending}>
      <label>Status inicial</label>
      <select value={status} onChange={(e) => setStatus(e.target.value as any)}>
        <option value="rodando">Rodando — entra na planilha com cobrança pendente e no site</option>
        <option value="a_implantar">A implantar — só custos; não fatura nem entra na planilha até ativar</option>
      </select>

      <label style={{ marginTop: 12 }}>Branch no GitHub</label>
      <select value={branchMode}
        onChange={(e) => { setBranchMode(e.target.value as any); setBranch(''); }}>
        <option value="create">Criar estrutura completa (branch nova a partir de uma estável)</option>
        <option value="existing">Branch já existe — usar os scripts como estão</option>
      </select>

      {branchMode === 'existing' ? (
        <>
          <label style={{ marginTop: 12 }}>Selecione a branch existente</label>
          <select value={branch} onChange={(e) => setBranch(e.target.value)}>
            <option value="">{branches ? 'selecione…' : 'carregando branches do git…'}</option>
            {(branches || []).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--ap-muted)' }}>
            Nome da arena, cidade e quadras serão lidos automaticamente dos
            camXTest.py da branch — nada é alterado no git.
          </p>
          <div style={{ marginTop: 12 }}>
            <label>Cidade exibição (site)</label>
            <input value={cityDisplay} onChange={(e) => setCityDisplay(e.target.value)} placeholder="Belo Horizonte" />
          </div>
        </>
      ) : (
        <>
          <label style={{ marginTop: 12 }}>Nome da arena (vira a pasta no bucket)</label>
          <input value={court} onChange={(e) => setCourt(e.target.value)} placeholder="Arena Nova" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <div>
              <label>Branch nova</label>
              <input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="arenanova" />
            </div>
            <div>
              <label>Branch base (estável)</label>
              <select value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)}>
                {!(branches || []).includes('prime') && <option value="prime">prime</option>}
                {(branches || []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label>Estado</label>
              <input value={state} onChange={(e) => setState(e.target.value)} placeholder="mg" />
            </div>
            <div>
              <label>Cidade (bucket, sem espaço)</label>
              <input value={cityBucket} onChange={(e) => setCityBucket(e.target.value)} placeholder="belohorizonte" />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label>Cidade exibição (site)</label>
            <input value={cityDisplay} onChange={(e) => setCityDisplay(e.target.value)} placeholder="Belo Horizonte" />
          </div>
          <div style={{ marginTop: 12 }}>
            <label>Quantas quadras?</label>
            <input type="number" min={1} max={6} value={numQuadras}
              onChange={(e) => setNum(Number(e.target.value))} />
          </div>
          {names.map((name, i) => (
            <div key={i} style={{ marginTop: 8 }}>
              <label>Nome da quadra {i + 1} (sem espaços)</label>
              <input value={name} onChange={(e) => {
                const next = [...names]; next[i] = e.target.value; setNames(next);
              }} placeholder={`Quadra${i + 1}`} />
            </div>
          ))}
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16 }}>
        <div>
          <label>Valor mensal (R$)</label>
          <input type="number" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="450" />
        </div>
        <div>
          <label>Dia de cobrança</label>
          <input type="number" min={1} max={28} value={diaCobranca}
            onChange={(e) => setDiaCobranca(e.target.value)} />
        </div>
        <div>
          <label>WhatsApp cobrança</label>
          <input value={contato} onChange={(e) => setContato(e.target.value)} placeholder="5531999999999" />
        </div>
      </div>
    </Modal>
  );
}
